/**
 * ConnectorGlyph — one shared, monochrome line/solid glyph per native connector.
 *
 * Renders a single connector's mark as `currentColor` (so the caller controls
 * the tint via CSS color) at a calm ~14-15px, with an optional corner health
 * dot: 'live' green, 'stale' amber, 'error' red, undefined = no dot. The glyph
 * paths are lifted from the desk design prototypes (Desk Detail "Monitoring N
 * sources" chips + Desk Settings "Connected sources" rows) so the in-app chips
 * match the handoff exactly.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * When adding a new native connector, add its monochrome glyph here.
 * Add a `case` to `glyphPath()` keyed on the connector/source id (and any
 * aliases, e.g. `gcal` / `google_calendar`). Keep it monochrome and currentColor
 * so it stays calm and inherits the chip's tint; do not introduce brand colors.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import type { ReactElement } from "react";
import "./connector-glyph.css";

export type ConnectorGlyphStatus = "live" | "stale" | "error";

export interface ConnectorGlyphProps {
  /** Connector / source id, e.g. "linear", "github", "gcal". */
  source: string;
  /** Glyph box size in px (the inner SVG scales to fit). Default 15. */
  size?: number;
  /**
   * Optional connector-state health. Renders a small corner dot:
   * 'live' green, 'stale' amber, 'error' red. Omit for no dot.
   */
  status?: ConnectorGlyphStatus;
  className?: string;
  /** Accessible label; defaults to the source id. Set "" to mark decorative. */
  title?: string;
}

/** A glyph definition: SVG inner markup + whether it's a stroked or filled mark. */
interface GlyphDef {
  /** SVG inner markup (paths/shapes), drawn in a 0 0 24 24 viewBox. */
  body: ReactElement;
  /** Stroked line glyph (default) vs solid filled glyph. */
  fill?: boolean;
  /** Per-glyph stroke width for stroked glyphs (default 2). */
  strokeWidth?: number;
}

// Stroked line marks share these attributes; solid marks set fill below.
const STROKE_PROPS = {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

function glyphDef(source: string): GlyphDef {
  switch (source.trim().toLowerCase()) {
    // Linear — three nested arcs (from the prototype "Monitoring sources" chip).
    case "linear":
      return {
        strokeWidth: 2,
        body: (
          <>
            <path d="M3 13a8 8 0 0 0 8 8" />
            <path d="M3 9a12 12 0 0 0 12 12" />
            <path d="M3 5a16 16 0 0 0 16 16" />
          </>
        ),
      };
    // Jira — the Atlassian four-arrow mark, stroked (aliases: jira_cloud).
    case "jira":
    case "jira_cloud":
      return {
        strokeWidth: 1.8,
        body: (
          <>
            <path d="M12 3.6c4.5 0 7.2 2.6 7.2 7.2 0 4.5-2.7 7.2-7.2 7.2-4.5 0-7.2-2.7-7.2-7.2 0-4.6 2.7-7.2 7.2-7.2Z" />
            <path d="M12 3.6v7.2h7.2" />
            <path d="M11.4 11.4 6.5 6.5" />
            <path d="M11.4 12.6 6.5 17.5" />
          </>
        ),
      };
    // GitHub — the octocat mark, solid (from the prototype).
    case "github":
      return {
        fill: true,
        body: (
          <path d="M12 2C6.5 2 2 6.5 2 12.1c0 4.4 2.9 8.2 6.8 9.5.5.1.7-.2.7-.5 0-.2 0-.9 0-1.7-2.8.6-3.4-1.3-3.4-1.3-.5-1.2-1.1-1.5-1.1-1.5-.9-.6.1-.6.1-.6 1 .1 1.5 1 1.5 1 .9 1.5 2.3 1.1 2.9.8.1-.6.3-1.1.6-1.3-2.2-.3-4.6-1.1-4.6-5 0-1.1.4-2 1-2.7-.1-.3-.4-1.3.1-2.7 0 0 .8-.3 2.7 1a9.4 9.4 0 0 1 5 0c1.9-1.3 2.7-1 2.7-1 .5 1.4.2 2.4.1 2.7.6.7 1 1.6 1 2.7 0 3.9-2.4 4.7-4.6 5 .4.3.7.9.7 1.9 0 1.3 0 2.4 0 2.8 0 .3.2.6.7.5 3.9-1.3 6.8-5.1 6.8-9.5C22 6.5 17.5 2 12 2Z" />
        ),
      };
    // Sentry — the chevron/peak mark, solid (from the prototype).
    case "sentry":
      return {
        fill: true,
        body: (
          <path d="M13 5.6a1.2 1.2 0 0 0-2 0L4.5 17.7c-.4.7.1 1.6.9 1.6h3.3a.6.6 0 0 0 .6-.7 6 6 0 0 0-2.6-4l1.2-2a8 8 0 0 1 3.7 6 .7.7 0 0 0 .7.7h4.8c.8 0 1.3-.9.9-1.6L13 5.6Z" />
        ),
      };
    // Figma — the five-part mark: two rounded halves on top, a half and a
    // circle in the middle, a drop at the foot, stroked.
    case "figma":
      return {
        strokeWidth: 1.8,
        body: (
          <>
            <path d="M12 3H9a3 3 0 0 0 0 6h3V3Z" />
            <path d="M12 3h3a3 3 0 0 1 0 6h-3V3Z" />
            <path d="M12 9H9a3 3 0 0 0 0 6h3V9Z" />
            <circle cx="15" cy="12" r="3" />
            <path d="M12 15H9a3 3 0 1 0 3 3v-3Z" />
          </>
        ),
      };
    // Slack — the four-bar hash mark (from the prototype).
    case "slack":
      return {
        strokeWidth: 2.4,
        body: <path d="M9 3.5v9M15 11.5v9M3.5 15h9M11.5 9h9" />,
      };
    // HubSpot — the sprocket/node mark.
    case "hubspot":
      return {
        strokeWidth: 1.8,
        body: (
          <>
            <circle cx="6" cy="18" r="2.5" />
            <circle cx="18" cy="8" r="2.5" />
            <path d="M16 9.6 8 16.4" />
            <path d="M18 10.5V14a3 3 0 0 1-3 3h-1" />
            <path d="M18 5.5V4" />
          </>
        ),
      };
    // Gmail — the open envelope.
    case "gmail":
      return {
        strokeWidth: 1.8,
        body: (
          <>
            <rect x="2.5" y="5" width="19" height="14" rx="2" />
            <path d="m3 7 9 6 9-6" />
          </>
        ),
      };
    // Google Calendar — calendar grid (aliases: gcal, google_calendar).
    case "gcal":
    case "google_calendar":
      return {
        strokeWidth: 1.8,
        body: (
          <>
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" />
          </>
        ),
      };
    // Google Drive — the triangle-of-shards mark.
    case "google_drive":
    case "gdrive":
      return {
        strokeWidth: 1.7,
        body: (
          <>
            <path d="m8 3 8 0 5 8.5-4 0z" />
            <path d="M8 3 3 11.5 7 18.5l4-7z" />
            <path d="m3.2 11.5 4 7 8 0 4-7z" />
          </>
        ),
      };
    // Calendly — a clock face (booked time).
    case "calendly":
      return {
        strokeWidth: 1.8,
        body: (
          <>
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7.5V12l3 2" />
          </>
        ),
      };
    // Notion — the page with a folded corner + rule lines.
    case "notion":
      return {
        strokeWidth: 1.8,
        body: (
          <>
            <path d="M5 3h9l5 5v13a0 0 0 0 1 0 0H5a0 0 0 0 1 0 0V3z" />
            <path d="M14 3v5h5" />
            <path d="M8 12h8M8 16h6" />
          </>
        ),
      };
    // Stripe — the slanted "S" bars.
    case "stripe":
      return {
        strokeWidth: 1.9,
        body: (
          <>
            <path d="M16 7.5c-1-.8-2.4-1.3-4-1.3-2.2 0-3.6 1-3.6 2.4 0 3.6 8 2 8 5.7 0 1.6-1.6 2.7-4 2.7-1.8 0-3.4-.6-4.4-1.5" />
          </>
        ),
      };
    // Intercom — the rounded speech panel with three notes.
    case "intercom":
      return {
        strokeWidth: 1.8,
        body: (
          <>
            <rect x="3" y="4" width="18" height="14" rx="3" />
            <path d="M8 21c1.3-1 2.6-1.6 4-2" />
            <path d="M8 9v3M12 9.5v4M16 9v3" />
          </>
        ),
      };
    // Neutral fallback — a generic "plug" mark for any unmapped connector.
    default:
      return {
        strokeWidth: 1.8,
        body: (
          <>
            <path d="M9 2v5M15 2v5" />
            <path d="M6 7h12v3a6 6 0 0 1-12 0z" />
            <path d="M12 16v6" />
          </>
        ),
      };
  }
}

/**
 * The monochrome connector mark. Inherits `color` (the glyph is `currentColor`),
 * so callers tint it via CSS. The optional health dot rides the bottom-right
 * corner exactly as the prototype draws it.
 */
export function ConnectorGlyph({
  source,
  size = 15,
  status,
  className,
  title,
}: ConnectorGlyphProps) {
  const def = glyphDef(source);
  const label = title === undefined ? source : title;
  // The SVG sits ~1px inside the box so the corner dot has room to overlap.
  const svgSize = Math.max(10, size - 1);
  return (
    <span
      className={`connector-glyph${className ? ` ${className}` : ""}`}
      style={{ width: size, height: size }}
      role={label ? "img" : undefined}
      aria-label={label || undefined}
      aria-hidden={label ? undefined : true}
    >
      <svg
        viewBox="0 0 24 24"
        width={svgSize}
        height={svgSize}
        {...(def.fill
          ? { fill: "currentColor" }
          : { ...STROKE_PROPS, strokeWidth: def.strokeWidth ?? 2 })}
      >
        {def.body}
      </svg>
      {status ? (
        <span className={`connector-glyph__dot connector-glyph__dot--${status}`} aria-hidden />
      ) : null}
    </span>
  );
}
